import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useEffect, useState, useCallback, memo} from 'react';
import {useNavigate} from 'extensions/navigation';
import {usePopup} from 'extensions/viewport/usePopup';
import {useSelector, useDispatch} from 'react-redux';
import {useWindowDimensions, StyleSheet, View, Text, Image, GestureResponderEvent} from 'react-native';
import {useHotkeys} from 'interface/hooks/useHotkeys';
import {useWelcome} from 'interface/hooks/useWelcome';
import {useMenuFolder} from 'interface/hooks/useMenuFolder';
import {FolderHeader} from 'interface/stacks/FolderHeader';
import {FolderToolbar} from 'interface/stacks/FolderToolbar';
import {FolderBreadcrumbs} from 'interface/stacks/FolderBreadcrumbs';
import {FileList} from 'interface/stacks/FileList';
import {Splash} from 'interface/stacks/Splash';
import {Loading} from 'interface/base/Loading';
import {Mouse} from 'interface/base/Mouse';
import {isWeb} from 'utils/platform';
import {testBit} from 'utils/common/data';
import device from 'extensions/device';
import images from 'config/images';
import {theme} from 'config/themes';
import {colors, vectors} from 'config/styles';
import {FilesItemFlag} from 'store/slices/files/types';
import {FileListItemLayout} from 'interface/stacks/FileListItem';
import {FolderTree} from 'interface/components/FolderTree';
import {load, isFolderSpecial} from 'utils/mediafire';
import {selectors} from 'store/slices/files';
import * as app from 'store/slices/app';

export interface FolderContentProps {
  id: string,
}

export const FolderContents = memo((props: FolderContentProps) => {
  const {t} = useLingui();
  const popup = usePopup();
  const [loaded, setLoaded] = useState(false);
  const isLoggedIn = useSelector(app.selectors.isLoggedIn);
  const isSearch = useSelector(selectors.isSearch);
  const filter = useSelector(selectors.getFilter);
  const items = useSelector(selectors.getItems);
  const query = useSelector(selectors.getQuery);
  const following = useSelector(selectors.getView('following'));
  const recent = useSelector(selectors.getView('recent'));
  const search = useSelector(selectors.getView('search'));
  const folder = useSelector(selectors.getItem(props.id));
  const view = useSelector(selectors.getView(props.id));
  const screen = useWindowDimensions();
  const isSpecial = isFolderSpecial(props.id);
  const menuFolder = useMenuFolder(props.id, folder?.name);

  const isTrash = props.id === 'trash';
  const isRecent = props.id === 'recent';
  const isFollowing = props.id === 'following';
  const isShared = !isSpecial && folder && !testBit(folder.flag, FilesItemFlag.isOwned);
  const isOwned = isLoggedIn && !isShared;
  const isDarkMode = device.isDarkMode();
  const isMinimal = screen.width < 600;
  const isLargeScreen = screen.width > 1120 && screen.height > 540;
  const isLoadingSearch = isSearch && !search && query;

  const sizeActual = view
    ? view.files.length + view.folders.length
    : 0;

  const sizeExpected = folder
    ? (filter.type === 'all' || !view ? folder.files : view.files.length)
    + (filter.type === 'all' || !view ? folder.folders : view.folders.length)
    : 0;

  const isLoadingFolder = isRecent
    ? !recent : isFollowing ? !following
    : (!folder || sizeActual === 0 && sizeExpected > 0);

  const hasHeader = !isOwned && !isTrash && !isLoadingFolder;
  const hasSpinner = isLoadingFolder || isLoadingSearch;

  const hasEmptyFolder = !isLoadingFolder &&
    (folder || (
      (isRecent || isFollowing || isShared)
      && sizeActual === 0
    ))
    && sizeExpected === 0
    && !query;
  
  const hasEmptySearch = isSearch
    && !isLoadingSearch
    && search
    && (search.files.length === 0 && search.folders.length === 0);
  
  const hasEmptyUploadTip = !isDarkMode
    && isLargeScreen
    && !isTrash
    && !isRecent
    && !isFollowing
    && !isShared;
  
  const hasListFolder = !isLoadingFolder
    && !hasEmptyFolder
    && !query;

  const hasListSearch = !isTrash
    && !isFollowing
    && !isLoadingFolder
    && !hasEmptySearch
    && !hasListFolder
    && search;
  
  const classes = {
    content: [
      styles.content,
      isMinimal && styles.minimal,
    ],
    uploadTip: [
      styles.uploadTip,
      hasEmptyUploadTip && styles.dragDrop,
    ],
    header: [
      styles.header,
      !hasEmptyUploadTip && styles.dragDropHeader,
    ],
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useHotkeys(props.id);
  useWelcome(isLoggedIn);

  // Load folder contents (this handles the folder access redirects)
  useEffect(() => {
    const isInit = !folder && props.id === 'myfiles';
    load(navigate, dispatch, props.id, isInit)
      .then(() => setLoaded(true));
    if (isLoggedIn) {
      load(navigate, dispatch, 'trash');
    }
  }, [props.id, navigate, dispatch]);

  if (isSpecial && !isLoggedIn) {
    return <Splash/>;
  }

  return (
    <View ref={menuFolder.reference} style={styles.root}>
      <Mouse onContextMenu={
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          popup.setPosition({x: event.pageX, y: event.pageY});
          popup.setRef(menuFolder.reference);
          menuFolder.open();
        }
      }>
        <View style={styles.contentContainer}>
          <FolderToolbar id={props.id} loaded={loaded}/>
          <View style={styles.mainContent}>
            {isOwned && !isMinimal && isWeb() &&
              <FolderTree/>
            }
            <View style={classes.content}>
              {hasHeader &&
                <FolderHeader id={props.id}/>
              }
              {isOwned &&
                <FolderBreadcrumbs id={props.id} search={query}/>
              }
              <View style={styles.lists}>
                {hasListFolder &&
                  <View style={styles.list}>
                    <FileList
                      view={view}
                      items={items}
                      layout={isShared
                        ? FileListItemLayout.GridSimple
                        : undefined
                      }
                      options={{
                        hideHeader: isRecent || isShared,
                        hideFolders: isRecent,
                        hideExtraIcons: isTrash,
                        disableOpen: isTrash,
                        disableDrag: isTrash || isFollowing,
                      }}
                    />
                  </View>
                }
                {hasListSearch && !hasEmptyFolder &&
                  <View style={styles.list}>
                    <FileList
                      view={search}
                      items={items}
                      options={{
                        linkParent: true,
                      }}
                    />
                  </View>
                }
                {hasEmptySearch &&
                  <View style={styles.center}>
                    <View style={styles.searchEmpty}>
                      <Svg viewBox={'0 0 200 200'} width={200} height={200}>
                        <Path d={vectors.artwork.searchNotFound[0]} fill="#8AC5FF"/>
                        <Path d={vectors.artwork.searchNotFound[1]} fill="#D2E9FF"/>
                        <Path d={vectors.artwork.searchNotFound[2]} fill="#99CCFF"/>
                        <Path d={vectors.artwork.searchNotFound[3]} fill="#A6D3FF"/>
                        <Path d={vectors.artwork.searchNotFound[4]} fill="#C0E0FF"/>
                        <Path d={vectors.artwork.searchNotFound[5]} fill="#D2E9FF"/>
                        <Path d={vectors.artwork.searchNotFound[6]} fill="#0070F0"/>
                        <Path d={vectors.artwork.searchNotFound[7]} fill="#80C0FF"/>
                      </Svg>
                      <Text style={styles.header}>
                        {t`No results found`}
                      </Text>
                      <Text style={styles.footer}>
                        {t`Sorry, we looked but didn't find anything.`}
                      </Text>
                    </View>
                  </View>
                }
                {hasEmptyFolder &&
                  <View style={styles.center}>
                    <View style={classes.uploadTip}>
                      {hasEmptyUploadTip &&
                        <Image style={styles.uploadTipBg} source={{uri: images.emptyBackground}}/>
                      }
                      {hasEmptyUploadTip &&
                        <Image style={styles.uploadTipDrag} source={{uri: images.emptyDrag}}/>
                      }
                      <Text style={classes.header}>
                        {isRecent
                          ? t`No recent files`
                          : isTrash
                              ? t`Trash is empty`
                              : isFollowing
                                ? t`You are not following anything`
                                : t`This folder is empty`
                        }
                      </Text>
                      {hasEmptyUploadTip &&
                        <Text style={styles.footer}>
                          {t`Upload files or folders by clicking the upload button, or drag &amp; drop from your device.`}
                        </Text>
                      }
                    </View>
                  </View>
                }
                {hasSpinner &&
                  <View style={styles.loading}>
                    <Loading/>
                  </View>
                }
              </View>
            </View>
          </View>
        </View>
      </Mouse>
    </View>
  )
});

export const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: theme.files.fill,
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  mainContent: {
    flex: 1,
    flexDirection: 'row',
  },
  content: {
    flex: 1,
  },
  minimal: {
    marginHorizontal: 0,
  },
  lists: {
    flex: 1,
    flexDirection: 'row',
  },
  list: {
    flex: 1,
    margin: 0,
    flexDirection: 'row',
  },
  header: {
    textAlign: 'center',
    color: colors.neutral.light,
    fontSize: 26,
  },
  footer: {
    textAlign: 'center',
    color: colors.neutral.mid,
    fontSize: 14,
    marginTop: 16,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchEmpty: {
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    flex: 1,
  },
  dragDrop: {
    width: 640,
    height: 360,
    padding: 36,
    borderRadius: 5,
    borderWidth: 5,
    borderStyle: 'dashed',
    borderColor: '#E6E6E6',
    position: 'relative',
  },
  dragDropHeader: {
    textAlign: 'center',
    color: colors.neutral.light,
    fontSize: 26,
  },
  uploadTip: {
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  uploadTipBg: {
    width: 559,
    height: 150,
    marginBottom: 26,
  },
  uploadTipDrag: {
    position: 'absolute',
    top: 100,
    left: 300,
    width: 58,
    height: 46,
    opacity: 1,
  },
});
